// Modern Uber-inspired table styling with advanced features
export const tableOptions = {
  paging: true,
  pageSize: 20,
  pageSizeOptions: [10, 20, 50, 100],
  search: true,
  searchFieldVariant: 'outlined',
  actionsColumnIndex: -1,
  sorting: true,
  draggable: false,
  filtering: false, // Enable per-table as needed
  columnsButton: true, // Show/hide columns toggle
  exportAllData: true,
  // Modern header styling - clean white with dark text
  headerStyle: {
    backgroundColor: '#FFFFFF',
    color: '#111827',
    fontWeight: 600,
    fontSize: 13,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: '2px solid #E5E7EB',
    padding: '12px 14px',
    whiteSpace: 'nowrap',
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  // Clean row styling
  rowStyle: {
    fontSize: 14,
    color: '#374151',
    borderBottom: '1px solid #F3F4F6',
  },
  // Cell padding - optimized for better space usage
  cellStyle: {
    padding: '10px 14px',
    fontSize: 14,
  },
  // Search field styling
  searchFieldStyle: {
    borderRadius: 8,
    backgroundColor: '#F9FAFB',
    padding: '8px 14px',
    fontSize: 14,
    minWidth: 280,
  },
  // Action buttons styling
  actionsCellStyle: {
    padding: '8px 16px',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
  },
  // Toolbar styling
  toolbarButtonAlignment: 'right',
  showTitle: true,
  // Pagination text
  emptyRowsWhenPaging: false,
  // Selection
  showSelectAllCheckbox: true,
  showTextRowsSelected: true,
};

// Options with filtering enabled
export const tableOptionsWithFiltering = {
  ...tableOptions,
  filtering: true,
  filterCellStyle: {
    padding: '8px 12px',
  },
};

// Options with export buttons enabled
export const tableOptionsWithExport = {
  ...tableOptions,
  exportButton: {
    csv: true,
    pdf: false,
  },
};

// Modern table container styles
export const tableContainerStyle = {
  borderRadius: 16,
  overflow: 'hidden',
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  border: '1px solid #E5E7EB',
  backgroundColor: '#FFFFFF',
};

// Modern action button styles - Edit (blue)
export const editButtonStyle = {
  minWidth: 36,
  height: 36,
  borderRadius: 8,
  padding: 0,
  backgroundColor: '#2563EB',
  color: '#FFFFFF',
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.15s ease',
  '&:hover': {
    backgroundColor: '#1D4ED8',
  },
};

// Delete button style (red)
export const deleteButtonStyle = {
  minWidth: 36,
  height: 36,
  borderRadius: 8,
  padding: 0,
  backgroundColor: '#DC2626',
  color: '#FFFFFF',
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.15s ease',
  '&:hover': {
    backgroundColor: '#B91C1C',
  },
};

// View button style (gray)
export const viewButtonStyle = {
  minWidth: 36,
  height: 36,
  borderRadius: 8,
  padding: 0,
  backgroundColor: '#F3F4F6',
  color: '#374151',
  border: '1px solid #E5E7EB',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.15s ease',
  '&:hover': {
    backgroundColor: '#E5E7EB',
    color: '#111827',
  },
};

// Table title styles
export const tableTitleStyle = {
  fontSize: 20,
  fontWeight: 700,
  color: '#111827',
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
};

// Empty state message styling
export const emptyStateStyle = {
  padding: '60px 20px',
  textAlign: 'center',
  color: '#9CA3AF',
  fontSize: 15,
};

// Pagination styling
export const paginationStyle = {
  borderTop: '1px solid #F3F4F6',
  padding: '12px 16px',
};

// Status badge styles
export const statusBadgeStyles = {
  active: {
    backgroundColor: '#DEF7EC',
    color: '#03543F',
    padding: '4px 12px',
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 600,
    display: 'inline-block',
  },
  inactive: {
    backgroundColor: '#FDE8E8',
    color: '#9B1C1C',
    padding: '4px 12px',
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 600,
    display: 'inline-block',
  },
  pending: {
    backgroundColor: '#FEF3C7',
    color: '#92400E',
    padding: '4px 12px',
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 600,
    display: 'inline-block',
  },
  default: {
    backgroundColor: '#F3F4F6',
    color: '#374151',
    padding: '4px 12px',
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 600,
    display: 'inline-block',
  },
};

// Filter toolbar styles
export const filterToolbarStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 16,
  padding: '16px 20px',
  backgroundColor: '#F9FAFB',
  borderBottom: '1px solid #E5E7EB',
  alignItems: 'flex-end',
};

// Filter input styles
export const filterSelectStyle = {
  minWidth: 180,
  '& .MuiOutlinedInput-root': {
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
  },
};

// Date picker styles
export const filterDateStyle = {
  minWidth: 160,
  '& .MuiOutlinedInput-root': {
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
  },
};

// Export buttons container
export const exportButtonsStyle = {
  display: 'flex',
  gap: 8,
  marginLeft: 'auto',
};

// Individual export button
export const exportButtonStyle = {
  padding: '8px 16px',
  borderRadius: 8,
  border: '1px solid #E5E7EB',
  backgroundColor: '#FFFFFF',
  color: '#374151',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  transition: 'all 0.15s ease',
  '&:hover': {
    backgroundColor: '#F3F4F6',
    borderColor: '#D1D5DB',
  },
};

// Results count text style
export const resultsCountStyle = {
  fontSize: 13,
  color: '#6B7280',
  padding: '12px 20px',
  borderBottom: '1px solid #F3F4F6',
};
